import { useEffect, useRef, useState } from "react";
import {
  angleDelta,
  angleFromNormalized,
  angleFromPointer,
  angleInSweep,
  clampAngleToSweep,
  DOUBLE_CLICK_WINDOW_MS,
  MIN_NORMALIZED_STEP,
  normalizedFromAngle,
  POINTER_LOCK_PIXELS_PER_FULL_SWEEP,
  pointerLockAcceleration,
  resolveRotaryKnobConfig,
  ROTARY_POINTER_OFFSET,
  supportsPointerLockApi,
  type RotaryKnobConfig,
  type RotaryKnobInteractionMode,
  type RotaryKnobResolvedConfig,
  clampValue,
} from "../lib/rotaryKnobMath";

export type RotaryKnobProps = {
  angle: number;
  onAngleChange?: (nextAngle: number) => void;
  onReset?: () => void;
  interactionMode?: RotaryKnobInteractionMode;
  config?: RotaryKnobConfig;
  stepNormalized?: number;
  disabled?: boolean;
  density?: "default" | "compact";
  size?: number;
  ariaLabel?: string;
  ariaValueMin?: number;
  ariaValueMax?: number;
  ariaValueNow?: number;
  ariaValueText?: string;
};

type DragState =
  | { mode: "directional"; angle: number; rawAngle: number; active: boolean }
  | { mode: "pointer-lock-vertical"; angle: number; rawAngle: number };

/**
 * React rotary knob — same interaction model as `rotaryKnob.ts` (pointer-lock
 * vertical drag preferred; directional arc fallback). Math stays in rotaryKnobMath.
 */
export function RotaryKnob({
  angle,
  onAngleChange,
  onReset,
  interactionMode = "pointer-lock-vertical",
  config,
  stepNormalized: stepNormalizedProp,
  disabled = false,
  density = "default",
  size,
  ariaLabel,
  ariaValueMin,
  ariaValueMax,
  ariaValueNow,
  ariaValueText,
}: RotaryKnobProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dialRef = useRef<HTMLSpanElement>(null);
  const resolvedRef = useRef<RotaryKnobResolvedConfig>(resolveRotaryKnobConfig(config));
  const dragRef = useRef<DragState | null>(null);
  const angleRef = useRef(angle);
  const pendingRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastDownRef = useRef(0);
  const onAngleChangeRef = useRef(onAngleChange);
  const onResetRef = useRef(onReset);
  const [displayAngle, setDisplayAngle] = useState(() =>
    clampAngleToSweep(angle, resolveRotaryKnobConfig(config)),
  );

  onAngleChangeRef.current = onAngleChange;
  onResetRef.current = onReset;
  resolvedRef.current = resolveRotaryKnobConfig(config);

  const stepNormalized =
    typeof stepNormalizedProp === "number"
    && Number.isFinite(stepNormalizedProp)
    && stepNormalizedProp > 0
      ? clampValue(stepNormalizedProp, MIN_NORMALIZED_STEP, 1)
      : null;

  useEffect(() => {
    if (dragRef.current) {
      return;
    }
    const next = clampAngleToSweep(angle, resolvedRef.current);
    angleRef.current = next;
    setDisplayAngle(next);
  }, [angle, config]);

  useEffect(() => {
    const dial = dialRef.current;
    if (dial) {
      dial.style.transform = `rotate(${displayAngle + ROTARY_POINTER_OFFSET}rad)`;
    }
  }, [displayAngle]);

  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
      teardownPointerLock();
      if (
        buttonRef.current
        && document.pointerLockElement === buttonRef.current
        && typeof document.exitPointerLock === "function"
      ) {
        document.exitPointerLock();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function queueAngleChange(nextAngle: number): void {
    pendingRef.current = nextAngle;
    if (rafRef.current !== null) {
      return;
    }
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending !== null) {
        onAngleChangeRef.current?.(pending);
      }
    });
  }

  function flushPendingAngle(): void {
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending !== null) {
      onAngleChangeRef.current?.(pending);
    }
  }

  function teardownPointerLock(): void {
    document.removeEventListener("mousemove", onDocMouseMove);
    document.removeEventListener("mouseup", onDocMouseUp);
    document.removeEventListener("pointerlockchange", onPointerLockChange);
    document.removeEventListener("pointerlockerror", onPointerLockError);
  }

  function clearPointerLockDrag(): void {
    teardownPointerLock();
    if (dragRef.current?.mode === "pointer-lock-vertical") {
      angleRef.current = dragRef.current.angle;
      dragRef.current = null;
    }
  }

  function onDocMouseMove(event: MouseEvent): void {
    const el = buttonRef.current;
    if (!el || document.pointerLockElement !== el) {
      return;
    }
    const drag = dragRef.current;
    if (!drag || drag.mode !== "pointer-lock-vertical") {
      return;
    }
    if (!Number.isFinite(event.movementY) || event.movementY === 0) {
      return;
    }
    const resolved = resolvedRef.current;
    const acceleration = pointerLockAcceleration(Math.abs(event.movementY), stepNormalized);
    const deltaAngle =
      (-event.movementY / POINTER_LOCK_PIXELS_PER_FULL_SWEEP)
      * resolved.sweepSigned
      * acceleration;
    const rawAngle = clampAngleToSweep(drag.rawAngle + deltaAngle, resolved);
    let nextAngle = rawAngle;
    if (stepNormalized) {
      const normalized = normalizedFromAngle(rawAngle, resolved);
      const snapped = clampValue(
        Math.round(normalized / stepNormalized) * stepNormalized,
        0,
        1,
      );
      nextAngle = angleFromNormalized(snapped, resolved);
    }
    if (nextAngle !== drag.angle) {
      queueAngleChange(nextAngle);
    }
    dragRef.current = { mode: "pointer-lock-vertical", angle: nextAngle, rawAngle };
    setDisplayAngle(nextAngle);
  }

  function onDocMouseUp(): void {
    flushPendingAngle();
    const el = buttonRef.current;
    if (el && document.pointerLockElement === el && typeof document.exitPointerLock === "function") {
      document.exitPointerLock();
      return;
    }
    clearPointerLockDrag();
  }

  function onPointerLockChange(): void {
    if (document.pointerLockElement === buttonRef.current) {
      return;
    }
    clearPointerLockDrag();
  }

  function onPointerLockError(): void {
    clearPointerLockDrag();
  }

  function pointerLockEnabled(): boolean {
    return interactionMode === "pointer-lock-vertical" && supportsPointerLockApi();
  }

  function handlePointerDown(event: React.PointerEvent<HTMLButtonElement>): void {
    if (disabled || !onAngleChange || event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const el = buttonRef.current;
    if (!el) {
      return;
    }

    if (onResetRef.current) {
      const now = typeof event.timeStamp === "number" ? event.timeStamp : Date.now();
      if (lastDownRef.current > 0 && now - lastDownRef.current <= DOUBLE_CLICK_WINDOW_MS) {
        lastDownRef.current = 0;
        onResetRef.current();
        return;
      }
      lastDownRef.current = now;
    }

    const resolved = resolvedRef.current;
    const clamped = clampAngleToSweep(angleRef.current, resolved);
    if (pointerLockEnabled()) {
      dragRef.current = { mode: "pointer-lock-vertical", angle: clamped, rawAngle: clamped };
      document.addEventListener("mousemove", onDocMouseMove);
      document.addEventListener("mouseup", onDocMouseUp);
      document.addEventListener("pointerlockchange", onPointerLockChange);
      document.addEventListener("pointerlockerror", onPointerLockError);
      try {
        const result = el.requestPointerLock();
        if (result && typeof (result as Promise<void>).catch === "function") {
          void (result as Promise<void>).catch(() => clearPointerLockDrag());
        }
      } catch {
        clearPointerLockDrag();
      }
      return;
    }

    el.setPointerCapture(event.pointerId);
    const rawAngle = angleFromPointer(event.nativeEvent, el);
    dragRef.current = {
      mode: "directional",
      angle: clamped,
      rawAngle,
      active: resolved.continuous || angleInSweep(rawAngle, resolved),
    };
  }

  function handlePointerMove(event: React.PointerEvent<HTMLButtonElement>): void {
    if (disabled || !onAngleChange) {
      return;
    }
    const drag = dragRef.current;
    const el = buttonRef.current;
    if (!drag || drag.mode !== "directional" || !el?.hasPointerCapture(event.pointerId)) {
      return;
    }
    const resolved = resolvedRef.current;
    const rawAngle = angleFromPointer(event.nativeEvent, el);
    if (!drag.active) {
      if (!angleInSweep(rawAngle, resolved)) {
        dragRef.current = { ...drag, rawAngle };
        return;
      }
      dragRef.current = { ...drag, rawAngle, active: true };
      return;
    }
    const delta = angleDelta(drag.rawAngle, rawAngle);
    const nextAngle = clampAngleToSweep(drag.angle + delta, resolved);
    queueAngleChange(nextAngle);
    dragRef.current = { ...drag, angle: nextAngle, rawAngle };
    setDisplayAngle(nextAngle);
  }

  function handlePointerUp(event: React.PointerEvent<HTMLButtonElement>): void {
    const el = buttonRef.current;
    if (dragRef.current?.mode === "pointer-lock-vertical") {
      flushPendingAngle();
      if (el && document.pointerLockElement === el && typeof document.exitPointerLock === "function") {
        document.exitPointerLock();
        return;
      }
      clearPointerLockDrag();
      return;
    }
    if (el?.hasPointerCapture(event.pointerId)) {
      el.releasePointerCapture(event.pointerId);
    }
    flushPendingAngle();
    if (dragRef.current?.mode === "directional") {
      angleRef.current = dragRef.current.angle;
    }
    dragRef.current = null;
  }

  return (
    <button
      ref={buttonRef}
      type="button"
      role="slider"
      className={[
        "rotary-knob relative flex shrink-0 items-center justify-center rounded-full border border-white/14 bg-white/[0.06] p-0 outline-none touch-none transition-[border-color,background,box-shadow] duration-100",
        density === "compact" ? "size-10" : "size-12",
        disabled
          ? "is-disabled cursor-not-allowed opacity-55"
          : "hover:border-[color-mix(in_srgb,var(--accent)_55%,var(--line))] hover:bg-white/[0.09] focus-visible:border-[color-mix(in_srgb,var(--accent)_70%,var(--line))] focus-visible:shadow-[0_0_0_1px_color-mix(in_srgb,var(--accent)_35%,transparent)]",
      ].join(" ")}
      style={size ? { width: size, height: size } : undefined}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-valuemin={ariaValueMin}
      aria-valuemax={ariaValueMax}
      aria-valuenow={ariaValueNow}
      aria-valuetext={ariaValueText}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <span
        className="rotary-knob-rim pointer-events-none absolute inset-0 rounded-[inherit]"
        aria-hidden={true}
      />
      <span
        className="rotary-knob-face absolute inset-0 rounded-[inherit] bg-[color-mix(in_srgb,var(--panel-deep)_88%,#000)] shadow-[inset_0_1px_3px_rgba(0,0,0,0.45)]"
        aria-hidden={true}
      />
      <span
        className="rotary-knob-sheen pointer-events-none absolute inset-[3px] rounded-[inherit] bg-gradient-to-b from-white/14 to-white/[0.03]"
        aria-hidden={true}
      />
      <span
        className="rotary-knob-dial pointer-events-none absolute inset-0 flex items-center justify-center"
        aria-hidden={true}
        ref={dialRef}
      >
        <span
          className={`rotary-knob-pointer absolute top-1/2 left-1/2 h-[40%] w-0.5 -ml-px -translate-y-full rounded-full ${
            disabled
              ? "bg-slate-500 shadow-none"
              : "bg-[color-mix(in_srgb,var(--accent)_70%,#e2e8f0)] shadow-[0_0_4px_color-mix(in_srgb,var(--accent)_35%,transparent)]"
          }`}
        />
      </span>
    </button>
  );
}
